import type { Bien } from "../types/bien";

/**
 * Patch de remise à zéro du cycle de sortie (ELS/PEB/SRS).
 *
 * ⚠️ Historique : appliqué au passage en LOUÉ jusqu'au 2026-07-10. Depuis la
 * relocation anticipée (un bail peut être signé AVANT la sortie du locataire
 * précédent), il est appliqué au passage LOUÉ → DISPONIBLE (le départ suivant)
 * + via le bouton « vider la fiche de sortie ». Miroir SQL : migration 085
 * (reset_exit_cycle_on_libere). Le nom est conservé pour éviter le churn.
 *
 * Démarre un nouveau cycle de suivi :
 *  - le prestataire ELS assigné (`prestataireEle`) devient le précédent (`prestataireEls`,
 *    trace pure) ET le champ assigné est VIDÉ (`prestataireEle = ""`). Plus de
 *    pré-remplissage : le C-Level ré-assigne DÉLIBÉRÉMENT au prochain cycle — c'est ce
 *    choix (et lui seul) qui déclenchera le mail + la visibilité (modèle 2026-06-06).
 *  - les infos de suivi ELS / PEB / SRS sont réinitialisées ;
 *  - l'expert PEB (`expertPeb`) est conservé (NON inclus dans le patch).
 *
 * Fonction pure : ne mute pas l'entrée, renvoie uniquement les champs modifiés.
 *
 * Paramètre réduit au minimum : seul `prestataireEle` est lu, donc Pick<Bien, "prestataireEle">
 * suffit — évite le double-cast `as unknown as Bien` chez l'appelant.
 */
export function resetElsCycleForLoue(bien: Pick<Bien, "prestataireEle">): Partial<Bien> {
  return {
    // Le cycle précédent est terminé : le suivant n'existera que par un nouveau
    // geste « Ouvrir un cycle » (migration 135, plan « deux cycles » Phase 2).
    // null, pas "" : colonne timestamptz, jamais bindée à un input texte.
    cycleOuvertLe: null,
    // Ouverture du cycle — sans ça, ces 3 dates du cycle PRÉCÉDENT survivaient
    // et pré-remplissaient à tort le dialogue "Ouvrir un cycle" du suivant,
    // sans venir de nulle part de visible (signalé Thibaud 2026-08-20 :
    // "elles sortent d'où ?"). Miroir SQL : reset_exit_cycle_on_libere.
    renonRecuLe: "",
    finDeContrat: "",
    seraDispoPour: "",
    // ELS — report prestataire : assigné → précédent (trace), puis champ assigné vidé.
    prestataireEls: bien.prestataireEle,
    prestataireEle: "",
    edlDatePrevue: "",
    edlHeure: "",
    statutEle: "",
    // Les champs ci-dessous sont émis en "" (jamais null) : TextAreaField / FormField /
    // DateField bindent `value` à ces champs et appellent `value.length` — null crasherait.
    // Le form normalise déjà tous ces champs en "" via `bien.xxx ?? ""`.
    edlCommentaire: "",
    transfertElecDate: "",
    transfertElecFait: false,
    appartPretPhotos: null, // tri-state boolean|null → rendu via select, null = "non renseigné"
    // État de cycle couplé (sinon hérité périmé) — cf. spec §5.
    // `statutEls` n'y figure plus : la colonne est supprimée (mig. 112), elle
    // était vide sur les 1574 biens. Le statut de l'ELS se CALCULE
    // (`computeStatutEls`) depuis prestataireEle / edlDatePrevue / statutEle,
    // qui sont bien remis à zéro ici.
    eleNonFait: false,
    prestataireEleRefusePar: null, // méta-interne, non bindé à un input texte
    prestataireEleRefuseAt: null,  // idem
    // PEB — expertPeb conservé
    pebDatePrevue: "",
    pebHeure: "",
    pebCommentaire: "",
    // Refus PEB (migration 068) — méta-interne, remis à null au nouveau cycle.
    expertPebRefusePar: null,
    expertPebRefuseAt: null,
    // PEB — cycle réinitialisé UNIQUEMENT au loué (statut + staging nouveau certificat).
    statutPeb: "",
    nouvClassePeb: "",
    nouvCertificatPeb: "",
    nouvPebFinLe: "",
    nouvConsoKwh: "",
    nouvEmissionCo2: "",
    nouvPebCertificatPdf: null,
    // SRS — tout vidé, flags compris.
    //
    // Les SIX montants du décompte détaillé (montantPreavis, montantDommages,
    // arrieresLoyer, montantEls, decompteCharges, autreMontant) ne figurent
    // plus ici : ils ont été retirés de la fiche le 2026-08-10 (le décompte se
    // résume au total). Ce patch ne doit NOMMER que des colonnes que le front
    // assume — sinon, le jour où la migration 107 les supprime, chaque passage
    // LOUÉ → DISPONIBLE échouerait sur l'UPDATE entier, pas sur un champ.
    totalSrs: "",
    commentaireSrs: "",
    indemnitePreavis: "",
    garantieLiberee: "",
    dossierCloture: false,
    dossierClos: false,
  };
}
